/**
 * Plain functions of the product catalogue pages (ADR-026): cycles, the price grid of a plan
 * (books × cycles, the price in force and the next one), the cycle discount shown next to a price,
 * the overdue calendar a product's settings give, and the checks the server makes on it. Kept free
 * of React so `tests/catalog.test.mjs` runs them in Node.
 */
import type {
  Branch,
  CalendarEvent,
  CatalogAddon,
  CatalogProduct,
  CatalogScope,
  ChosenOption,
  CycleCode,
  EffectiveLifecycle,
  FreeDomainOperation,
  FulfilProblem,
  FulfilSeverity,
  LifecycleSettings,
  OptionCondition,
  OptionGroup,
  OptionItemVisibility,
  OptionKind,
  PlacementGroup,
  PlanPriceRow,
  PlanSpec,
  PriceBookRef,
  PromotionAction,
  PromotionScopeKind,
  PublishProblem,
  RuleType,
  SaleMode,
  ServicePromotion,
  StockMode,
  Visibility,
} from '../api/areas/catalog';
import type { BilingualText, ModuleChoice, ModuleOptionRender } from '../api/areas/modules';

/** Every cycle, shortest first (catalog_core::BillingCycle): one-time, the hour (ADR-032 §2.3), then months. */
export const CYCLES: readonly CycleCode[] = ['ONCE', 'H1', 'M1', 'M3', 'M6', 'M12', 'M24', 'M36', 'M48', 'M60'];

/** The reasons a plan cannot go on sale (catalog_products::Problem and pricing's publish_problems). */
export const PROBLEM_CODES: readonly PublishProblem['code'][] = [
  'NO_STOCK',
  'NO_FULFILMENT',
  'NO_ACCEPTING_MEMBER',
  'PLACEMENT_BRANCH_MISMATCH',
  'PRODUCT_RETIRED',
  'NO_RETAIL_BOOK',
  'NO_PRICE',
  'NO_TAX_RATE',
  'NO_TAX_CODE',
  'SALE_MODE_MISMATCH',
];

export const CALENDAR_EVENTS: readonly CalendarEvent[] = ['RENEWAL_ORDER', 'REMINDER', 'LATE_FEE', 'SUSPEND', 'TERMINATE'];

/** Months of a cycle (`null`: one-time and the hour, ADR-032 §2.3). */
export function cycleMonths(code: CycleCode): number | null {
  return code.startsWith('M') ? Number(code.slice(1)) : null;
}

/** Cycles in their natural order. */
export function sortCycles(codes: readonly CycleCode[]): CycleCode[] {
  return [...new Set(codes)].sort((a, b) => CYCLES.indexOf(a) - CYCLES.indexOf(b));
}

/**
 * The discount of a cycle's price against the monthly price × months, in whole percent (tino.vn
 * shows "−20%" next to the annual price). `null` when there is no monthly price, for the monthly
 * cycle itself and when the cycle costs at least monthly × months.
 */
export function cycleDiscount(monthly: string | null | undefined, amount: string, code: CycleCode): number | null {
  const months = cycleMonths(code);
  if (!monthly || months === null || months <= 1) return null;
  const m = Number(monthly);
  const a = Number(amount);
  if (!(m > 0) || !Number.isFinite(a)) return null;
  const full = m * months;
  const pct = Math.round((1 - a / full) * 100);
  return pct > 0 ? pct : null;
}

export interface PriceCell {
  /** The window in force at `now`. */
  current: PlanPriceRow | null;
  /** The next window scheduled after `now`. */
  next: PlanPriceRow | null;
  /** How many versions the book and cycle had. */
  versions: number;
}

/** The plan's price grid: for each book (in the order given) and cycle, the window in force and the next one. */
export function priceGrid(rows: readonly PlanPriceRow[], books: readonly PriceBookRef[], cycles: readonly CycleCode[], now: string): Map<string, Map<CycleCode, PriceCell>> {
  const out = new Map<string, Map<CycleCode, PriceCell>>();
  for (const b of books) {
    const byCycle = new Map<CycleCode, PriceCell>();
    for (const c of cycles) {
      const mine = rows.filter((r) => r.price_book_id === b.id && r.cycle === c);
      const current = mine.find((r) => r.effective_from <= now && (r.effective_until === null || r.effective_until > now)) ?? null;
      const next =
        mine.filter((r) => r.effective_from > now).sort((x, y) => (x.effective_from < y.effective_from ? -1 : 1))[0] ?? null;
      byCycle.set(c, { current, next, versions: mine.length });
    }
    out.set(b.id, byCycle);
  }
  return out;
}

/** "758400.000000" → "758.400" (whole đồng, Vietnamese grouping). */
export function formatVnd(amount: string | null | undefined): string {
  if (amount === null || amount === undefined || amount === '') return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** Digits of an amount typed with Vietnamese grouping ("1.910.400", "1 910 400") as a decimal string. */
export function parseVnd(typed: string): string | null {
  const digits = typed.replace(/[.\s,]/g, '').trim();
  return /^\d{1,15}$/.test(digits) ? String(Number(digits)) : null;
}

/** The settings in force for a product: its own fields, else the group's, else the global ones. */
export function resolveLifecycle(own: Partial<LifecycleSettings> | null, group: Partial<LifecycleSettings> | null, global: LifecycleSettings): EffectiveLifecycle {
  const keys = [
    'invoice_days_before',
    'reminder_days',
    'payment_due_days_before',
    'suspend_enabled',
    'suspend_after_days',
    'terminate_enabled',
    'terminate_after_days',
    'keep_backups_days',
    'auto_debit_wallet',
  ] as const;
  const sources: EffectiveLifecycle['sources'] = {};
  const pick = <K extends (typeof keys)[number]>(k: K): NonNullable<LifecycleSettings[K]> => {
    const fromOwn = own?.[k];
    if (fromOwn !== null && fromOwn !== undefined) {
      sources[k] = 'PRODUCT';
      return fromOwn as NonNullable<LifecycleSettings[K]>;
    }
    const fromGroup = group?.[k];
    if (fromGroup !== null && fromGroup !== undefined) {
      sources[k] = 'GROUP';
      return fromGroup as NonNullable<LifecycleSettings[K]>;
    }
    sources[k] = 'GLOBAL';
    return global[k] as NonNullable<LifecycleSettings[K]>;
  };
  // The late fee is one setting: percent and day together, from the first level that sets it.
  const lateFrom = [own, group, global].find((s) => s?.late_fee_percent);
  const late = lateFrom ? { p: lateFrom.late_fee_percent ?? null, d: lateFrom.late_fee_after_days ?? null } : { p: null, d: null };
  sources.late_fee_percent = lateFrom === own && own ? 'PRODUCT' : lateFrom === group && group ? 'GROUP' : 'GLOBAL';
  return {
    invoice_days_before: pick('invoice_days_before'),
    reminder_days: pick('reminder_days'),
    payment_due_days_before: pick('payment_due_days_before'),
    suspend_enabled: pick('suspend_enabled'),
    suspend_after_days: pick('suspend_after_days'),
    terminate_enabled: pick('terminate_enabled'),
    terminate_after_days: pick('terminate_after_days'),
    keep_backups_days: pick('keep_backups_days'),
    late_fee_percent: late.p,
    late_fee_after_days: late.d,
    auto_debit_wallet: pick('auto_debit_wallet'),
    sources,
  };
}

/** The overdue calendar (days from the due date; negative = before), as catalog_products shows it. */
export function lifecycleCalendar(e: EffectiveLifecycle): { day: number; event: CalendarEvent }[] {
  const out: { day: number; event: CalendarEvent }[] = [{ day: -e.invoice_days_before, event: 'RENEWAL_ORDER' }];
  for (const d of e.reminder_days) out.push({ day: -d, event: 'REMINDER' });
  if (e.late_fee_percent && e.late_fee_after_days !== null) out.push({ day: e.late_fee_after_days, event: 'LATE_FEE' });
  if (e.suspend_enabled) out.push({ day: e.suspend_after_days, event: 'SUSPEND' });
  if (e.terminate_enabled) out.push({ day: e.terminate_after_days, event: 'TERMINATE' });
  const rank = (ev: CalendarEvent) => (ev === 'RENEWAL_ORDER' ? 0 : 1);
  return out.sort((a, b) => a.day - b.day || rank(a.event) - rank(b.event) || a.event.localeCompare(b.event));
}

/** What the server would refuse in a calendar (catalog_products::lifecycle): keys of `catalog:lifecycle.problem`. */
export function lifecycleProblems(e: EffectiveLifecycle): ('terminateBeforeSuspend' | 'payByBeforeOrder' | 'reminderBeforeOrder')[] {
  const out: ('terminateBeforeSuspend' | 'payByBeforeOrder' | 'reminderBeforeOrder')[] = [];
  if (e.suspend_enabled && e.terminate_enabled && e.terminate_after_days <= e.suspend_after_days) out.push('terminateBeforeSuspend');
  if (e.payment_due_days_before > e.invoice_days_before) out.push('payByBeforeOrder');
  if (e.reminder_days.some((d) => d > e.invoice_days_before)) out.push('reminderBeforeOrder');
  return out;
}

/** Reminder days typed as "14, 7, 3, 1, 0, -3" (sorted from the earliest, no duplicates); `null` when unreadable. */
export function parseReminderDays(typed: string): number[] | null {
  const parts = typed.split(/[,;\s]+/).filter(Boolean);
  if (parts.some((p) => !/^[-−]?\d{1,3}$/.test(p))) return null;
  const days = [...new Set(parts.map((p) => Number(p.replace('−', '-'))))];
  if (days.some((d) => d < -365 || d > 365) || days.length > 16) return null;
  return days.sort((a, b) => b - a);
}

// ---------------------------------------------------------------------------------------------
// The editor's closed sets: what the server accepts (catalog_products::admin, catalog_products::options,
// commerce_pricing::service::admin); each has a name in plain words in both languages
// (`tests/catalog-editor.test.mjs`).

export const VISIBILITIES: readonly Visibility[] = ['DRAFT', 'PUBLIC', 'HIDDEN', 'RETIRED'];
export const BRANCHES: readonly Branch[] = ['dns', 'hosting', 'email', 'cloud', 'website'];
export const FULFILMENTS: readonly CatalogProduct['fulfilment'][] = ['PROVISIONED', 'MANUAL'];
export const SERVICE_LABELS: readonly CatalogProduct['service_label'][] = ['DOMAIN', 'HOSTNAME', 'NONE'];
export const OPTION_KINDS: readonly OptionKind[] = ['SELECT', 'RADIO', 'CHECKBOX', 'QUANTITY', 'TEXT', 'SSH_KEY', 'HIDDEN'];
/** Kinds whose groups list choices (the others take a number or a text). */
export const KINDS_WITH_ITEMS: readonly OptionKind[] = ['SELECT', 'RADIO', 'CHECKBOX', 'QUANTITY', 'HIDDEN'];
export const CONDITION_EFFECTS: readonly OptionCondition['effect'][] = ['HIDE', 'REQUIRE'];
export const ITEM_VISIBILITIES: readonly OptionItemVisibility[] = ['PUBLIC', 'HIDDEN', 'RETIRED'];
export const ADDON_BILLINGS: readonly CatalogAddon['billing'][] = ['RECURRING', 'ONCE'];
export const ADDON_FULFILMENTS: readonly CatalogAddon['fulfilment'][] = ['MODULE_OP', 'MANUAL', 'NONE'];
export const FREE_DOMAIN_OPERATIONS: readonly FreeDomainOperation[] = ['REGISTER', 'TRANSFER'];
export const STRATEGIES: readonly PlacementGroup['strategy'][] = ['LEAST_USED', 'FILL_FIRST', 'FIXED'];
export const STOCK_MODES: readonly StockMode[] = ['UNLIMITED', 'COUNTED', 'CAPACITY', 'CLOSED'];
/** How a group sells its plans (ADR-032 §2.2). */
export const SALE_MODES: readonly SaleMode[] = ['CYCLE', 'HOURLY'];
export const TAX_CODES = ['VAT8', 'VAT10', 'KCT'] as const;
/** The option keys each branch engine reads from `maps_to` (`HostingOptionKey`, `CloudOptionKey`, `WebsiteOptionKey` of the branches' contracts, ADR-032 §2.7). */
export const OPTION_KEYS: Readonly<Record<Branch, readonly string[]>> = {
  dns: [],
  hosting: ['dedicated_ip'],
  email: [],
  cloud: ['image', 'ssh_keys', 'ipv4', 'ipv6'],
  website: ['template'],
};
export const RULE_TYPES: readonly RuleType[] = ['DISCOUNT_PERCENT', 'FIXED_PRICE'];
export const CATALOG_SCOPES: readonly CatalogScope[] = ['GROUP', 'PRODUCT', 'PLAN'];
export const PROMOTION_SCOPES: readonly PromotionScopeKind[] = ['TLD', 'GROUP', 'PRODUCT', 'PLAN'];
export const PROMOTION_ACTIONS: readonly PromotionAction[] = ['NEW', 'RENEW', 'UPGRADE'];

/** A product, group or location code: lower-case letters, digits and '-', starting with a letter or digit. */
export function codeOk(v: string, max = 60): boolean {
  return v.length > 0 && v.length <= max && /^[a-z0-9][a-z0-9-]*$/.test(v);
}

/** A group's code starts with a letter (catalog_products::admin::create_group). */
export function groupCodeOk(v: string): boolean {
  return codeOk(v, 40) && /^[a-z]/.test(v);
}

/** An option group's or item's code: lower-case letters, digits, '_' and '-', starting with a letter. */
export function optionCodeOk(v: string): boolean {
  return v.length > 0 && v.length <= 40 && /^[a-z][a-z0-9_-]*$/.test(v);
}

/** A code suggested from a name ("Hệ điều hành" → "he-dieu-hanh"); the operator can change it. */
export function suggestCode(name: string, max = 60): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, max)
    .replace(/-+$/, '');
}

/** Specs as the server checks them: each with a key, both labels and a value; a new row's key comes from its label. */
export function specsOut(rows: readonly { key?: string; label_vi: string; label_en: string; value: string; unit?: string }[]): PlanSpec[] {
  const used = new Set<string>();
  return rows
    .filter((r) => r.label_vi.trim() || r.value.trim())
    .map((r) => {
      let key = r.key || suggestCode(r.label_en || r.label_vi, 40).replace(/-/g, '_') || 'spec';
      for (let n = 2; used.has(key); n += 1) key = `${key.replace(/_\d+$/, '')}_${n}`;
      used.add(key);
      const out: PlanSpec = { key, label_vi: r.label_vi.trim(), label_en: (r.label_en || r.label_vi).trim(), value: r.value.trim() };
      if (r.unit?.trim()) out.unit = r.unit.trim();
      return out;
    });
}

/** Cycles a plan may offer: at least one; a one-time or an hourly plan offers nothing else (catalog_core::cycles_ok). */
export function cyclesProblem(cycles: readonly CycleCode[]): 'none' | 'onceAlone' | 'hourAlone' | null {
  if (cycles.length === 0) return 'none';
  if (cycles.includes('ONCE') && cycles.length > 1) return 'onceAlone';
  if (cycles.includes('H1') && cycles.length > 1) return 'hourAlone';
  return null;
}

/**
 * What the server refuses in an option group's conditions (catalog_products::options::parse_conditions
 * and option_group_ok): a group the product does not have, no triggering choice, an unknown effect.
 * Keys of `catalog:editor.conditionProblem`.
 */
export function conditionProblems(conditions: readonly OptionCondition[], siblings: readonly { code: string }[]): ('unknownGroup' | 'noTrigger' | 'badEffect')[] {
  const out = new Set<'unknownGroup' | 'noTrigger' | 'badEffect'>();
  for (const c of conditions) {
    if (!siblings.some((s) => s.code === c.when_group)) out.add('unknownGroup');
    if (c.when_items.length === 0) out.add('noTrigger');
    if (!CONDITION_EFFECTS.includes(c.effect)) out.add('badEffect');
  }
  return [...out];
}

/** Why a promotion's catalogue scope cannot change (commerce_pricing::service::admin::set_promotion_scope); keys of `catalog:promoScope.blocked`. */
export function promotionScopeBlocked(p: Pick<ServicePromotion, 'disabled_at' | 'redemptions' | 'tld' | 'operation'>): 'disabled' | 'used' | 'domain' | null {
  if (p.disabled_at) return 'disabled';
  if (p.redemptions > 0) return 'used';
  if (p.tld || p.operation) return 'domain';
  return null;
}

/** A rule's value as typed: a percent of at most 100 or whole đồng; `null` when unreadable. */
export function ruleValue(type: RuleType, typed: string): string | null {
  if (type === 'FIXED_PRICE') return parseVnd(typed);
  const v = typed.replace(',', '.').replace('%', '').trim();
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(v)) return null;
  return Number(v) <= 100 ? String(Number(v)) : null;
}

/** A percent typed as "5", "12,5" or "12.5%" (at most 999.9999, `NUMERIC(7,4)`); `null` when unreadable. */
export function percentValue(typed: string): string | null {
  const v = typed.replace(',', '.').replace('%', '').trim();
  return /^\d{1,3}(\.\d{1,4})?$/.test(v) ? String(Number(v)) : null;
}

// ---------------------------------------------------------------------------------------------
// Module connection of a plan (ADR-032 §2.4–2.9)

/** The kind of an option group drawn as a module's option type renders it. */
export function optionKindOf(render: ModuleOptionRender): OptionKind {
  return render.kind === 'radio' ? 'RADIO' : render.kind === 'quantity' ? 'QUANTITY' : 'SELECT';
}

/** A value a list action gave on some members of a server group; `missing`: the members without it (ADR-032 §2.6). */
export interface MemberChoice {
  value: string;
  label: BilingualText;
  missing: string[];
}

/**
 * The values of a list action run on every accepting member (`choices: null`: the member did not
 * answer, so no value is known there). A value is offered only when `missing` is empty; the others
 * are shown greyed with the members that lack them. Values on every member come first.
 */
export function commonChoices(lists: readonly { key: string; choices: readonly ModuleChoice[] | null }[]): MemberChoice[] {
  const out = new Map<string, MemberChoice>();
  for (const l of lists) for (const c of l.choices ?? []) if (!out.has(c.value)) out.set(c.value, { value: c.value, label: c.label, missing: [] });
  for (const c of out.values()) c.missing = lists.filter((l) => !l.choices?.some((x) => x.value === c.value)).map((l) => l.key);
  return [...out.values()].sort((a, b) => a.missing.length - b.missing.length);
}

/** The rows of "Sẵn sàng bán" on a plan's module connection, in the order of the tab (ADR-032 §2.11). */
export const FULFIL_CONDITIONS = ['module', 'members', 'facts', 'plan', 'settings', 'options', 'capacity'] as const;
export type FulfilCondition = (typeof FULFIL_CONDITIONS)[number];

/**
 * The row each problem code fails: catalog_core::CommonProblem (10), the hosting core's
 * NO_SERVER_DATA and NO_CAPACITY, and NOT_SENT / UNKNOWN / REFUSED of a member whose server was not
 * read by a live check. A code of another branch core falls under `settings`. Keys of `catalog:fulfil.problem`.
 */
export const FULFIL_PROBLEM_CONDITION: Readonly<Record<string, FulfilCondition>> = {
  GROUP_MODULE_MISMATCH: 'module',
  MEMBER_NOT_FOUND: 'members',
  MEMBER_DISABLED: 'members',
  FIXED_NEEDS_ONE: 'members',
  FACTS_MISSING: 'facts',
  FACTS_STALE: 'facts',
  NO_SERVER_DATA: 'facts',
  NOT_SENT: 'facts',
  UNKNOWN: 'facts',
  REFUSED: 'facts',
  PLAN_NOT_FOUND: 'plan',
  SETTINGS_INVALID: 'settings',
  NOT_OVERRIDABLE: 'settings',
  VALUE_NOT_FOUND: 'options',
  NO_CAPACITY: 'capacity',
};

/** Each row of "Sẵn sàng bán" with its problems: BLOCK when one blocks, WARN when only warnings, else OK. */
export function fulfilReadiness(problems: readonly FulfilProblem[]): { condition: FulfilCondition; state: 'OK' | FulfilSeverity; problems: FulfilProblem[] }[] {
  return FULFIL_CONDITIONS.map((condition) => {
    const mine = problems.filter((p) => (FULFIL_PROBLEM_CONDITION[p.code] ?? 'settings') === condition);
    return { condition, state: mine.some((p) => p.severity === 'BLOCK') ? 'BLOCK' : mine.length ? 'WARN' : 'OK', problems: mine };
  });
}

/** What staff picked in one option group: an item, a quantity or a text, as typed. */
export interface OptionPick {
  item?: string;
  qty?: string;
  text?: string;
}

/** The option groups offered with a plan (`plans` null: every plan of the product). */
export function optionGroupsOf(groups: readonly OptionGroup[], planId: string): OptionGroup[] {
  return groups.filter((g) => g.plans === null || g.plans.includes(planId));
}

/**
 * The choices sent with a quote and an order (`options`, catalog_core::options::ChosenOption). A
 * group left as it is is not sent: the server takes its default item, or its minimum quantity; so
 * is a quantity that is not a whole number (`qty` is an `i32`).
 */
export function chosenOptions(groups: readonly OptionGroup[], picks: Readonly<Record<string, OptionPick>>): ChosenOption[] {
  return groups.flatMap((g): ChosenOption[] => {
    const p = picks[g.code];
    if (!p) return [];
    if (g.kind === 'QUANTITY') return /^\d+$/.test(p.qty?.trim() ?? '') ? [{ group: g.code, qty: Number(p.qty) }] : [];
    if (g.kind === 'TEXT' || g.kind === 'SSH_KEY') return p.text?.trim() ? [{ group: g.code, text: p.text.trim() }] : [];
    return p.item ? [{ group: g.code, item: p.item }] : [];
  });
}
